/**
 * TikTok Pixel helper.
 *
 * The Pixel ID is a PUBLIC identifier (it is visible in the browser on every
 * site that uses TikTok ads), so it is safe to keep in the codebase.
 *
 * 1) Get it from TikTok Ads Manager -> Assets -> Events -> Web Events
 *    -> Manual install (looks like "CXXXXXXXXXXXXXXXXXXX").
 * 2) Paste it below (or set VITE_TIKTOK_PIXEL_ID).
 *
 * Until an ID is set, every call here is a safe no-op.
 */
export const TIKTOK_PIXEL_ID: string =
  (import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined) || '';

declare global {
  interface Window {
    ttq?: any;
    TiktokAnalyticsObject?: string;
  }
}

let loaded = false;

/** Injects the official TikTok Pixel base code once. */
export const loadTikTokPixel = (): void => {
  if (loaded || !TIKTOK_PIXEL_ID || typeof window === 'undefined') return;
  loaded = true;

  (function (w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ];
    ttq.setAndDefer = function (obj: any, method: string) {
      obj[method] = function () {
        obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (id: string) {
      const inst = (ttq._i && ttq._i[id]) || [];
      for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(inst, ttq.methods[i]);
      return inst;
    };
    ttq.load = function (id: string, opts?: any) {
      const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      const optionsObj = opts || {};
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._i[id]._u = url;
      ttq._t = ttq._t || {};
      ttq._t[id] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[id] = optionsObj;
      const script = d.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${url}?sdkid=${id}&lib=${t}`;
      const first = d.getElementsByTagName('script')[0];
      first?.parentNode?.insertBefore(script, first);
    };

    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, 'ttq');
};

const withPixel = (fn: (ttq: any) => void) => {
  if (!TIKTOK_PIXEL_ID || typeof window === 'undefined') return;
  loadTikTokPixel();
  if (window.ttq) fn(window.ttq);
};

export const ttPageView = (): void => withPixel((ttq) => ttq.page());

export const ttTrack = (event: string, params: Record<string, unknown> = {}): void =>
  withPixel((ttq) => ttq.track(event, params));

type PixelItem = {
  content_id: string;
  content_name?: string;
  price?: number;
  quantity?: number;
};

export const ttViewContent = (item: PixelItem): void =>
  ttTrack('ViewContent', {
    contents: [{ content_type: 'product', quantity: 1, ...item }],
    value: item.price,
    currency: 'USD',
  });

export const ttAddToCart = (item: PixelItem): void =>
  ttTrack('AddToCart', {
    contents: [{ content_type: 'product', quantity: 1, ...item }],
    value: item.price,
    currency: 'USD',
  });

export const ttInitiateCheckout = (items: PixelItem[], value: number): void =>
  ttTrack('InitiateCheckout', {
    contents: items.map((i) => ({ content_type: 'product', quantity: 1, ...i })),
    value,
    currency: 'USD',
  });

export const ttCompletePayment = (items: PixelItem[], value: number): void =>
  ttTrack('CompletePayment', {
    contents: items.map((i) => ({ content_type: 'product', quantity: 1, ...i })),
    value,
    currency: 'USD',
  });
